import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ChevronDown, Zap, ArrowRight } from "lucide-react";
import { Button } from "../components/ui";
import { Link } from "react-router-dom";
import { device } from "../lib/breakpoints";
import { APP_NAME, PERFORMANCE_FEE_DISCLAIMER } from "../helpers/constants";
import { trackPageViewOnce, trackPortalCta } from "../lib/siteActivity";

const comparisonData = [
  {
    category: "Upfront Cost",
    solvtax: "$0",
    traditional: "Hourly Retainer",
    diy: "Weeks of Admin Time",
  },
  {
    category: "Audit Velocity",
    solvtax: "60 Seconds",
    traditional: "3–6 Weeks",
    diy: "Variable",
  },
  {
    category: "Risk Profile",
    solvtax: "Zero Risk",
    traditional: "Pay Regardless of Outcome",
    diy: "Missed Deadlines",
  },
];

const faqs = [
  {
    question: "How can SolvTax audit my assets in under 60 seconds?",
    answer:
      "Our engine uses specialized compliance models tailored to local tax jurisdictions. The moment you upload your equipment ledger and county notice, the system automatically cross-references your asset descriptions against state-mandated depreciation schedules and percent-good tables to catch misclassifications instantly.",
  },
  {
    question: "How is my business property data secured?",
    answer:
      "All corporate tax filings and asset depreciation ledgers are managed within your encrypted, password-protected SolvTax Portal. We require corporate identity validation to protect sensitive financial records.",
  },
  {
    question: "What is your 35% performance-fee structure?",
    answer:
      "SolvTax is entirely risk-free for BPP audits. If we cannot secure a verified reduction to your Business Personal Property tax bill, your fee is $0. We only capture 35% of the actual, realized cash savings from depreciation schedule corrections and phantom-asset removals.",
  },
  {
    question: "What documents do I need to provide?",
    answer:
      "A PDF of your latest County Business Personal Property Assessment Notice and your internal equipment asset ledger or depreciation schedule (PDF or CSV) from your accounting system, covering machinery, tools, furniture, and similar personal property, not buildings or land.",
  },
];

const Pricing = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  useEffect(() => {
    trackPageViewOnce("/pricing", "Pricing");
  }, []);

  return (
    <Page>
      <Shell>
        <Header>
          <MainTitle>35% Performance Fee. Zero Upfront Risk.</MainTitle>
          <HeaderSubtitle>
            SolvTax charges a 35% performance fee only on verified Business Personal Property (BPP)
            tax-bill reductions: machinery, equipment, and asset ledgers, not real estate. If we
            don&apos;t reduce your assessment, you pay&nbsp;$0.
          </HeaderSubtitle>
        </Header>

        <hr className="my-16 border-t border-slate-800/60 max-w-5xl mx-auto" />

        <ComparisonBlock>
          <SectionLabel>Fee Structure</SectionLabel>
          <SectionTitle>How {APP_NAME} Compares</SectionTitle>

          <ComparisonTableContainer>
            <ComparisonTable>
              <thead>
                <ComparisonTableRow $isHeader>
                  <ComparisonTableHeader $isCategory>Metric</ComparisonTableHeader>
                  <ComparisonTableHeader $isSolvTax>{APP_NAME}</ComparisonTableHeader>
                  <ComparisonTableHeader>Traditional Firm</ComparisonTableHeader>
                  <ComparisonTableHeader>DIY</ComparisonTableHeader>
                </ComparisonTableRow>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <ComparisonTableRow key={row.category} $isEven={index % 2 === 0}>
                    <ComparisonTableCell $isCategory>{row.category}</ComparisonTableCell>
                    <ComparisonTableCell $isSolvTax>{row.solvtax}</ComparisonTableCell>
                    <ComparisonTableCell>{row.traditional}</ComparisonTableCell>
                    <ComparisonTableCell>{row.diy}</ComparisonTableCell>
                  </ComparisonTableRow>
                ))}
              </tbody>
            </ComparisonTable>
          </ComparisonTableContainer>
        </ComparisonBlock>

        <FaqBlock className="max-w-3xl mx-auto w-full px-4">
          <SectionLabel>FAQ</SectionLabel>
          <SectionTitle>Frequently Asked Questions</SectionTitle>

          <FaqList>
            {faqs.map((faq, index) => (
              <FaqItem key={faq.question} $open={openFaqIndex === index}>
                <FaqButton
                  type="button"
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  aria-expanded={openFaqIndex === index}
                >
                  {faq.question}
                  <FaqChevron $open={openFaqIndex === index}>
                    <ChevronDown className="h-5 w-5" />
                  </FaqChevron>
                </FaqButton>
                <FaqAnswer $open={openFaqIndex === index}>
                  <p className="text-left text-slate-600 dark:text-slate-400 text-sm leading-relaxed pt-2 pb-4 px-1">
                    {faq.answer}
                  </p>
                </FaqAnswer>
              </FaqItem>
            ))}
          </FaqList>
        </FaqBlock>

        <CtaBlock>
          <CtaTitle>Ready to run a 60-second BPP diagnostic?</CtaTitle>
          <CtaSubtitle>{PERFORMANCE_FEE_DISCLAIMER}</CtaSubtitle>
          <CtaActions>
            <Link to="/?triggerSignup=true" onClick={() => trackPortalCta("pricing-cta")}>
              <CtaButton size="xl">
                <Zap className="h-5 w-5" />
                Request Access
                <ArrowRight className="h-5 w-5" />
              </CtaButton>
            </Link>
            <Link to="/contact">
              <CtaOutlineButton variant="outline" size="lg">
                Talk to Us
              </CtaOutlineButton>
            </Link>
          </CtaActions>
        </CtaBlock>
      </Shell>
    </Page>
  );
};

const Page = styled.div`
  min-height: 100vh;
  background: #0f172a;
  color: #e2e8f0;
`;

const Shell = styled.div`
  max-width: 80rem;
  margin: 0 auto;
  padding: 5rem 2rem;
  width: 100%;
  box-sizing: border-box;

  @media (${device.tablet}) {
    padding-left: 3rem;
    padding-right: 3rem;
  }
`;

const Header = styled.header`
  text-align: center;
  max-width: 52rem;
  margin-left: auto;
  margin-right: auto;
`;

const MainTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #f8fafc;
  margin: 0 0 1.25rem;
  letter-spacing: -0.025em;
  line-height: 1.2;

  @media (min-width: 640px) {
    font-size: 2.5rem;
  }

  @media (min-width: 1024px) {
    font-size: 2.875rem;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 1.0625rem;
  line-height: 1.7;
  color: #94a3b8;
  margin: 0;
  max-width: 56ch;
  margin-left: auto;
  margin-right: auto;
  text-wrap: balance;

  @media (min-width: 1024px) {
    font-size: 1.125rem;
  }
`;

const SectionLabel = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #22d3ee;
  margin: 0 0 12px;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 2rem;
  letter-spacing: -0.025em;
  line-height: 1.2;
  color: #f8fafc;

  @media (min-width: 1024px) {
    font-size: 2rem;
    margin-bottom: 2.5rem;
  }
`;

const ComparisonBlock = styled.section`
  margin-bottom: 5rem;
`;

const ComparisonTableContainer = styled.div`
  overflow-x: auto;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(30, 41, 59, 0.8);
  border-radius: 0.75rem;
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const ComparisonTableRow = styled.tr`
  border-bottom: 1px solid rgba(30, 41, 59, 0.6);

  ${(p) =>
    p.$isHeader &&
    `
    border-bottom: 1px solid rgba(51, 65, 85, 0.8);
  `}

  ${(p) =>
    p.$isEven &&
    !p.$isHeader &&
    `
    background: rgba(15, 23, 42, 0.35);
  `}

  &:last-child {
    border-bottom: none;
  }
`;

const ComparisonTableHeader = styled.th`
  text-align: ${(p) => (p.$isCategory ? "left" : "center")};
  padding: 1rem 1.25rem;
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.02em;
  color: ${(p) => (p.$isSolvTax ? "#22d3ee" : p.$isCategory ? "#e2e8f0" : "#94a3b8")};

  @media (min-width: 768px) {
    padding: 1.125rem 1.5rem;
    font-size: 0.9375rem;
  }
`;

const ComparisonTableCell = styled.td`
  padding: 1rem 1.25rem;
  text-align: ${(p) => (p.$isCategory ? "left" : "center")};
  font-size: 0.875rem;
  line-height: 1.5;
  color: ${(p) => (p.$isSolvTax ? "#22d3ee" : p.$isCategory ? "#e2e8f0" : "#94a3b8")};
  font-weight: ${(p) => (p.$isCategory || p.$isSolvTax ? "600" : "400")};

  @media (min-width: 768px) {
    padding: 1.125rem 1.5rem;
    font-size: 0.9375rem;
  }
`;

const FaqBlock = styled.section`
  margin-bottom: 5rem;
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FaqItem = styled.div`
  border: 1px solid ${(p) => (p.$open ? "#22d3ee" : "rgba(51, 65, 85, 0.7)")};
  background: ${(p) => (p.$open ? "rgba(15, 23, 42, 0.65)" : "rgba(15, 23, 42, 0.35)")};
  border-radius: 0.5rem;
  transition:
    border-color 0.2s,
    background 0.2s;
`;

const FaqButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  text-align: left;
  padding: 18px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  color: #f8fafc;
`;

const FaqChevron = styled.span`
  flex-shrink: 0;
  transition: transform 0.2s;
  transform: ${(p) => (p.$open ? "rotate(180deg)" : "none")};
  color: ${(p) => (p.$open ? "#22d3ee" : "#64748b")};
`;

const FaqAnswer = styled.div`
  overflow: hidden;
  max-height: ${(p) => (p.$open ? "400px" : "0")};
  transition: max-height 0.25s ease;
  text-align: left;
`;

const CtaBlock = styled.section`
  text-align: center;
  padding-top: 2rem;
  border-top: 1px solid rgba(51, 65, 85, 0.6);
`;

const CtaTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #f8fafc;
  margin: 0 0 12px;
  letter-spacing: -0.02em;
`;

const CtaSubtitle = styled.p`
  font-size: 1rem;
  color: #94a3b8;
  margin: 0 0 28px;
  max-width: 42ch;
  margin-left: auto;
  margin-right: auto;
  white-space: pre-line;
`;

const CtaActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
  justify-content: center;

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const CtaButton = styled(Button)`
  background: #22d3ee !important;
  color: #0b1220 !important;
  border: none !important;
  font-weight: 700 !important;
  border-radius: 0 !important;
  display: inline-flex !important;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #67e8f9 !important;
  }
`;

const CtaOutlineButton = styled(Button)`
  border: 1px solid rgba(148, 163, 184, 0.5) !important;
  color: #e2e8f0 !important;
  background: transparent !important;

  &:hover {
    border-color: #22d3ee !important;
    color: #22d3ee !important;
  }
`;

export default Pricing;
